import { A11yModule } from '@angular/cdk/a11y';
import {
  AsyncPipe,
  CurrencyPipe,
  DatePipe,
  LocationStrategy,
  NgClass,
  NgIf,
  registerLocaleData,
} from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import localeAR from '@angular/common/locales/ar';
import localeBG from '@angular/common/locales/bg';
import localeDA from '@angular/common/locales/da';
import localeDE from '@angular/common/locales/de';
import localeEN from '@angular/common/locales/en';
import localeES from '@angular/common/locales/es';
import localeESMX from '@angular/common/locales/es-MX';
import localeESPE from '@angular/common/locales/es-PE';
import localeFi from '@angular/common/locales/fi';
import localeFR from '@angular/common/locales/fr';
import localeIT from '@angular/common/locales/it';
import localeJA from '@angular/common/locales/ja';
import localeKO from '@angular/common/locales/ko';
import localeNB from '@angular/common/locales/nb';
import localeNL from '@angular/common/locales/nl';
import localePT from '@angular/common/locales/pt';
import localeRU from '@angular/common/locales/ru';
import localeSV from '@angular/common/locales/sv';
import localeZH from '@angular/common/locales/zh';
import { APP_INITIALIZER, CSP_NONCE, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyProgressBarModule } from '@angular/material/legacy-progress-bar';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { GRPC_SEARCH_CLIENT_SETTINGS } from '@grpc-clients/services/attractions-service/api/search.pbconf';
import { GRPC_BOOK_CLIENT_SETTINGS } from '@grpc-clients/services/booking-service/api/book.pbconf';
import { GRPC_PREBOOK_CLIENT_SETTINGS } from '@grpc-clients/services/booking-service/api/prebook.pbconf';
import { GRPC_DINING_CLIENT_SETTINGS } from '@grpc-clients/services/dining-service/api/dining.pbconf';
import { GRPC_ENTERTAINMENT_CLIENT_SETTINGS } from '@grpc-clients/services/entertainments-service/api/entertainment.pbconf';
import { EffectsModule as NgrxEffectsModule } from '@ngrx/effects';
import { MetaReducer, StoreModule as NgrxStoreModule, provideStore } from '@ngrx/store';
import { GRPC_INTERCEPTORS, GrpcCoreModule, GrpcLoggerModule } from '@ngx-grpc/core';
import { GrpcWebClientModule } from '@ngx-grpc/grpc-web-client';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ChatComponent } from '@shared/components/chat/chat/chat.component';
import { ResizeDirective } from '@shared/directives/resize.directive';
import { MaterialManifestModule } from '@shared/material-manifest/material-manifest.module';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';
import { CustomDatePipe } from '@shared/pipes/custom-date.pipe';
import { GenericApiErrorsInterceptorService } from '@shared/services/generic-api-errors-interceptor.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { CLIENT_PATH } from '@shared/shared/injection-tokens';
import { MapModule } from '@shared/shared/modules/map/map.module';
import { LanguageService } from '@shared/shared/services/language-service';
import { WINDOW } from '@shared/shared/tokens/window';
import { StoreModule } from '@shared/store/store.module';
import { Angulartics2Module } from 'angulartics2';
import { AppComponent } from 'app.component';
import { BreadcrumbComponent } from 'core/components/breadcrumb/breadcrumb.component';
import { FooterComponent } from 'core/components/footer/footer.component';
import { GlobalNavComponent } from 'core/components/global-nav/global-nav.component';
import { LoggedOutNavContentComponent } from 'core/components/logged-out-nav-content/logged-out-nav-content.component';
import { LoggedOutNavComponent } from 'core/components/logged-out-nav/logged-out-nav.component';
import { ManagerSpecialRequestComponent } from 'core/components/manager-special-request/manager-special-request.component';
import { MtlsFooterComponent } from 'core/components/mtls-footer/mtls-footer.component';
import { SitemapFooterComponent } from 'core/components/sitemap-footer/sitemap-footer.component';
import { TenConciergeFooterComponent } from 'core/components/ten-concierge-footer/ten-concierge-footer.component';
import { AcceptLanguageInterceptorService } from 'core/services/accept-language-interceptor';
import { ContentTypeInterceptorService } from 'core/services/content-type-interceptor.service';
import { TraceIdInterceptor } from 'core/services/trace-id-interceptor';
import { UnauthorizedGrpcResponseInterceptor } from 'core/services/unauthorised-grpc-response-interceptor';
import { UnauthorisedResponseInterceptorService } from 'core/services/unauthorised-response-interceptor.service';
import { environment } from 'environment';
import { storeFreeze } from 'ngrx-store-freeze';
import { CookieService } from 'ngx-cookie-service';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { Observable } from 'rxjs';
import { AppInitializerService } from './app-initializer.service';
import { AppRoutingModule } from './app-routing.module';
import { reducers } from './shared/store';

registerLocaleData(localeAR, 'ar');
registerLocaleData(localeBG, 'bg');
registerLocaleData(localeDA, 'da');
registerLocaleData(localeDE, 'de');
registerLocaleData(localeEN, 'en');
registerLocaleData(localeES, 'es');
registerLocaleData(localeESMX, 'es-MX');
registerLocaleData(localeESPE, 'es-pe');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localeFR, 'fr');
registerLocaleData(localeIT, 'it');
registerLocaleData(localeJA, 'ja');
registerLocaleData(localeKO, 'ko');
registerLocaleData(localeNB, 'nb');
registerLocaleData(localeNL, 'nl');
registerLocaleData(localePT, 'pt');
registerLocaleData(localeRU, 'ru');
registerLocaleData(localeSV, 'sv');
registerLocaleData(localeZH, 'zh');

class TranslationLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  public getTranslation(lang: string): Observable<any> {
    if (lang.length <= 2) {
      lang = 'en-gb';
    }
    return this.http.get(`/static-content/shared/${lang}/translations.json?t=${new Date().getTime()}`);
  }
}

const metaReducers: MetaReducer<any>[] = !environment.production ? [storeFreeze] : [];

const nonceValue = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('value');
// For use else where, like script.service.ts, where the nonce is needed to load scripts
localStorage.setItem('nonce', nonceValue);

const translationLoader = {
  provide: TranslateLoader,
  useClass: TranslationLoader,
  deps: [HttpClient, LocationStrategy],
};

// compiler sometimes throws error when using standalone app component as bootstrap component,
// so we need to declare app component here until app module is removed
const APP_COMPONENT_IMPORTS = [
  MatLegacyProgressBarModule,
  AsyncPipe,
  NgIf,
  GlobalNavComponent,
  BreadcrumbComponent,
  LoggedOutNavComponent,
  LoggedOutNavContentComponent,
  NgClass,
  RouterModule,
  ManagerSpecialRequestComponent,
  TourMatMenuModule,
  MatLegacyCardModule,
  A11yModule,
  MatLegacyCheckboxModule,
  FormsModule,
  TranslateModule,
  MatLegacyButtonModule,
  MatIconModule,
  SitemapFooterComponent,
  FooterComponent,
  TenConciergeFooterComponent,
  MtlsFooterComponent,
  ChatComponent,
  ResizeDirective,
];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    StoreModule,
    AppRoutingModule,
    MaterialManifestModule,
    LoadingBarHttpClientModule,
    LoadingBarModule,
    HttpClientModule,
    MapModule,
    TranslateModule.forRoot({
      loader: translationLoader,
    }),
    GrpcCoreModule.forRoot(),
    GrpcWebClientModule.forRoot(),
    !environment.production ? GrpcLoggerModule.forRoot({ settings: { logStatusCodeOk: true } }) : [],
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    HttpClientXsrfModule.withOptions({
      cookieName: 'csrftoken',
      headerName: 'X-CSRFToken',
    }),
    RouterModule,
    Angulartics2Module.forRoot(),
    ...APP_COMPONENT_IMPORTS,
    NgrxStoreModule.forRoot(reducers, { metaReducers }),
    NgrxEffectsModule.forRoot([]),
  ],
  exports: [MaterialManifestModule],
  bootstrap: [AppComponent],
  providers: [
    AppInitializerService,
    {
      provide: CSP_NONCE,
      useValue: nonceValue,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (appInitializerService: AppInitializerService) => {
        return () => appInitializerService.initializeApplication();
      },
      deps: [AppInitializerService],
      multi: true,
    },
    {
      provide: CLIENT_PATH,
      useFactory: (siteSettingsService: SiteSettingsService) => {
        const path = siteSettingsService?.settings?.fe_configuration?.path;
        if (!path) {
          console.error(
            'Could not find client path in SiteSettingsService.settings. Make sure to set it during initialization.'
          );
        }
        return path;
      },
      deps: [SiteSettingsService],
    },
    {
      provide: LOCALE_ID,
      useFactory: (languageService: LanguageService) => languageService.currentLanguage,
      deps: [LanguageService],
    },
    { provide: WINDOW, useValue: window },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorisedResponseInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AcceptLanguageInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ContentTypeInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GenericApiErrorsInterceptorService,
      multi: true,
    },
    {
      provide: GRPC_INTERCEPTORS,
      useClass: TraceIdInterceptor,
      multi: true,
    },
    {
      provide: GRPC_INTERCEPTORS,
      useClass: UnauthorizedGrpcResponseInterceptor,
      multi: true,
    },
    { provide: GRPC_SEARCH_CLIENT_SETTINGS, useValue: { host: '' } },
    { provide: GRPC_PREBOOK_CLIENT_SETTINGS, useValue: { host: '' } },
    { provide: GRPC_BOOK_CLIENT_SETTINGS, useValue: { host: '' } },
    { provide: GRPC_ENTERTAINMENT_CLIENT_SETTINGS, useValue: { host: '' } },
    { provide: GRPC_DINING_CLIENT_SETTINGS, useValue: { host: '' } },
    Title,
    CookieService,
    CustomCurrencyPipe,
    CustomDatePipe,
    DatePipe,
    CurrencyPipe,
    provideStore(reducers, { metaReducers }),
  ],
})
export class AppModule {}
